import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Flag, MapPin } from 'lucide-react';
import { useDeviceCourses } from '../context/DeviceCoursesContext';
import CourseSearchPanel from '../components/CourseSearchPanel';

/**
 * Pre-round screen: the user picks which of their saved courses they are
 * playing, then presses Play to begin. For now this hands off to the existing
 * Upload page; eventually Play will take over that flow.
 */
const PlayPage: React.FC = () => {
  const navigate = useNavigate();
  const { savedCourses, selectedCourseId, selectCourse, hydrated } = useDeviceCourses();

  const handleCourseSelected = (courseId: string) => {
    selectCourse(courseId);
    navigate('/upload');
  };

  const playHeader = (
    <header className="play-header">
      <img
        src={`${process.env.PUBLIC_URL}/wholelogo-navy.svg`}
        alt="CaddieSight — See the shot. Own the swing."
        className="play-header__logo"
      />
      <h1>Let's Play</h1>
      <p>Pick the course you're playing, then capture your shot.</p>
    </header>
  );

  // No saved courses once the profile pull has finished: drop the golfer straight
  // into course search so they can find a course and start playing without a
  // detour through the Profile page.
  if (hydrated && savedCourses.length === 0) {
    return (
      <div className="play-page">
        {playHeader}
        <CourseSearchPanel />
      </div>
    );
  }

  return (
    <div className="play-page">
      {playHeader}
      <section className="card-surface play-course-card">
        <h2 className="play-section-title">
          <MapPin className="ui-icon" strokeWidth={1.5} aria-hidden="true" />
          Select a course
        </h2>

        {savedCourses.length === 0 ? (
          <div className="play-empty">
            <p>Loading your saved courses…</p>
          </div>
        ) : (
          <>
            <ul className="play-course-list">
              {savedCourses.map((c) => (
                <li
                  key={c.id}
                  className={c.id === selectedCourseId ? 'selected' : ''}
                  role="button"
                  tabIndex={0}
                  aria-pressed={c.id === selectedCourseId}
                  onClick={() => handleCourseSelected(c.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCourseSelected(c.id);
                    }
                  }}
                >
                  <div className="play-course-info">
                    <span className="play-course-name">{c.name}</span>
                    <span className="play-course-loc">{[c.city, c.state].filter(Boolean).join(', ')}</span>
                    <span className="play-course-meta">Par {c.par} · {c.holes} holes</span>
                  </div>
                  {c.id === selectedCourseId && (
                    <span className="play-course-status">
                      <Flag className="ui-icon" strokeWidth={1.5} aria-hidden="true" />
                      Playing
                    </span>
                  )}
                  <ChevronRight className="play-course-chevron" strokeWidth={1.5} aria-hidden="true" />
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
};

export default PlayPage;
